"use client";

import Image from "next/image";
import classNames from "classnames";
import { Title } from "@/components/atoms/Title";
import { Paragraph } from "@/components/atoms/Paragraph";
import { Testimonies } from "@/components/molecules/Testimonies";
import { PorangeButton } from "@/components/atoms/PorangeButton";
import { SecondaryTitle } from "@/components/atoms/SecondaryTitle";
import { SecondaryParagraph } from "@/components/atoms/SecondaryParagraph";
import { SocialProofAvatars } from "@/components/molecules/SocialProofAvatars";
import { PricingCard } from "@/components/organisms/PricingCard";
import { getNoise } from "@/utils/noise";
import { MIN_SOCIAL_PROOF, MAX_SOCIAL_PROOF } from "@/constants/noise";
import { Inter } from "next/font/google";
import { useParams } from 'next/navigation'
import { useEffect, useMemo } from "react";
import { VETTED_PLACES_WORLDWIDE } from "@/constants";
import { useStripeContext } from "@/context/StripeContextProvider";
import { useQuizDataContext } from "@/context/QuizDataContextProvider";

const interFont = Inter({ subsets: ["latin"] });

export const TestimoniesData = [
    {
        id: 2,
        text: "As someone with celiac disease, the safety rankings are super helpful. I love being able to filter for only celiac-friendly and 100% gluten-free restaurants.",
        imageSrc: "/assets/avatars/Avatar user-1.png",
        username: "Jane G.",
        glutenFreeLevel: "Highly symptomatic celiac",
        stars: 5,
    },
    {
        id: 1,
        text: "I have been using it for 3 months and all I can say is.. YESSSS! I don't feel the need to call and double check every time. So much less complicated.",
        imageSrc: "/assets/avatars/Avatar user-5.png",
        username: "Kenneth M.",
        glutenFreeLevel: "Gluten Intolerant",
        stars: 5,
    },
    {
        id: 3,
        text: "The color-coded pins make it so easy to find places. Saves me so much time. I love this map!!",
        imageSrc: "/assets/avatars/Avatar user-3.png",
        username: "Sammy F.",
        glutenFreeLevel: "Gluten Preference",
        stars: 5,
    },
    {
        id: 4,
        text: "Love how reviews from different platforms are in one place! And there is a dedicated team behind the updates, gives me peace of mind.",
        imageSrc: "/assets/avatars/Avatar user-2.png",
        username: "Derrick T.",
        glutenFreeLevel: "Symptomatic celiac",
        stars: 5,
    },
    {
        id: 5,
        text: "Knowing the safety rankings were developed with dietitians gives me peace of mind. The recommendation counts on pins super nifty too!",
        imageSrc: "/assets/avatars/Avatar user-4.png",
        username: "Shantal L.",
        glutenFreeLevel: "Symptomatic celiac",
        stars: 5,
    }
];

const Article = ({ children, noMargin, className }) => {
    return (
        <article className={classNames({ "m-auto": !noMargin }, className)}>
            {children}
        </article>
    );
};

export const PreCheckoutPage = ({
    isDisabled = false,
    formSubmit = () => { },
    trialVariation = false,
    promotionAmountOff = 0,
    userEmail = "",
    branchUrl = "",
    onEmailChange = () => { },
}) => {
    const params = useParams();
    const { isAppTrial } = useQuizDataContext();
    const noise = getNoise("glutenfreeq", {
        min: MIN_SOCIAL_PROOF,
        max: MAX_SOCIAL_PROOF,
        range: MAX_SOCIAL_PROOF - MIN_SOCIAL_PROOF,
    });

    const immediateFlow = params?.ad === "ad";
    const { stripeData } = useStripeContext();
    const { clientSecret, customerId } = stripeData;

    const isInfluencerFlow = useMemo(() => {
        return clientSecret && customerId;
    }, [clientSecret, customerId]);

    const buttonCtaText = useMemo(() => {
        return trialVariation ? "Start 7 day free trial" : isAppTrial ? "Download App" : "Subscribe";
    }, [trialVariation, isAppTrial]);

    const disclaimer = useMemo(() => {
        if (isDisabled) {
            return "";
        }

        if (promotionAmountOff) {
            return `$${promotionAmountOff} off promo applied ✅`;
        }

        if (immediateFlow || !trialVariation) {
            return "";
        }

        return "Charged today";
    }, [promotionAmountOff, immediateFlow, trialVariation, isDisabled]);

    const price = useMemo(() => {
        if (isDisabled) {
            return "";
        }

        if (promotionAmountOff) {
            return "";
        }

        if (immediateFlow || !trialVariation) {
            return "";
        }

        return "$0.00";
    }, [promotionAmountOff, immediateFlow, trialVariation, isDisabled]);

    return (
        <div className="flex flex-col w-full max-w-screen-md 
        mx-auto px-5 pt-5 pb-24 h-dvh md:h-full overflow-y-auto overflow-x-hidden">
            <Article className="w-full">
                <Title className="mb-4">Eat out with confidence!</Title>
                <Paragraph className="mb-6">
                    Quickly find gluten-free restaurants, bakeries, and cafés worldwide.
                </Paragraph>
            </Article>
            <Article className="mb-2 w-full md:w-fit">
                <PricingCard
                    title="Gluten Free Eats"
                    disabled={isDisabled}
                    userEmail={userEmail}
                    onEmailChange={onEmailChange}
                    branchUrl={branchUrl}
                    withTrial={trialVariation}
                    buttonText={immediateFlow ? "Let's go" : buttonCtaText}
                    price={price}
                    disclaimer={disclaimer}
                    onClick={formSubmit}
                    features={[
                        `${VETTED_PLACES_WORLDWIDE} vetted places worldwide`,
                        "50+ filters and safety criterias",
                        "Up to date information",
                    ]}
                />
            </Article>
            <Article className="flex items-center justify-center gap-2 text-[13px] font-medium dark:text-blue-900 mb-10">
                <SocialProofAvatars
                    count={5}
                    avatarsBorderColor="white"
                />
                <span className={classNames(interFont.className, "font-medium")}>
                    {noise.value} joined today 👆
                </span>
            </Article>
            <Article className="mb-10">
                <SecondaryTitle className="mb-4">
                    Built with dietitians and celiac experts
                </SecondaryTitle>
                <SecondaryParagraph className="mb-6">
                    We equip you with important information about each place to reduce the
                    possibilities of getting sick or going hungry.
                </SecondaryParagraph>
                <Image
                    src="/assets/gluten-free/celiac-experts.png"
                    alt="Celiac experts"
                    className="m-auto"
                    width={335}
                    height={251.25}
                />
            </Article>
            <Testimonies testimonies={TestimoniesData} />
            <Article className="my-10">
                <PorangeButton
                    className="h-14"
                    showLoader={!isInfluencerFlow}
                    disabled={isDisabled}
                    onClick={event => {
                        if (isInfluencerFlow) {
                            event.preventDefault();
                            const emailElement = document.getElementById("card-payment-email");
                            if (emailElement) {
                                emailElement.scrollIntoView({ behavior: "smooth" });
                            }
                        } else {
                            formSubmit(event);
                        }
                    }}>
                    {isInfluencerFlow ? "Scroll back up" : immediateFlow ? "Let's go" : buttonCtaText}
                </PorangeButton>
            </Article>
            <Article className="mb-10">
                <div className="rounded-[12px] overflow-hidden">
                    <Image
                        src="/assets/gluten-free/powered-by-stripe.png"
                        alt="Powered by Stripe"
                        width={309}
                        height={44}
                    />
                </div>
            </Article>
        </div>
    );
};
